import { faPenToSquare } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Typography, IconButton, useTheme, Grid } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import DialogActions from "src/containers/DialogActions/DialogActions";
import FilterByDays from "src/containers/FilterByDays/FilterByDays";
import useChangeDays from "src/containers/FilterByDays/useChangeDays/useChangeDays";
import LoadingOverlay from "src/containers/LoadingOverlay/LoadingOverlay";
import AddEditComment from "src/containers/MyTeamPresenceAndLeaves/MyTeamBreaks/AddEditComment/AddEditComment";
import {
  AnomalyTypesQuickfilter,
  useAnomalyTypesQuickFilter,
} from "src/containers/Quickfilters/AnomalyTypesQuickfilter";

import {
  NotificationStatusQuickfilter,
  useNotificationStatusQuickFilter,
} from "src/containers/Quickfilters/NotificationStatusQuickfilter";
import QuickfiltersContainer from "src/containers/Quickfilters/QuickfiltersContainer/QuickfiltersContainer";
import useAuthorization from "src/custom-hooks/useAuthorization/useAuthorization";
import { CustomDate } from "src/models/custom-date/custom-date";
import { EmployeeRef } from "src/models/employees";
import { AnomalyTrackingCommentSTO } from "src/models/tna/tna-anomalies/anomaly-tracking-comment-sto";
import { AnomalyTrackingDTO } from "src/models/tna/tna-anomalies/anomaly-tracking-dto";
import { AnomalyTrackingFilter } from "src/models/tna/tna-anomalies/anomaly-tracking-filter";
import Table from "src/reusable-components/Table/Table";
import { SendAnomalyTrackingNotificationToEmployeesApiService } from "src/services/send-anomaly-tracking-notifications-to-employees/send-anomaly-tracking-notifications-to-employees-api.service";
import { AnomalyTrackingByEmployee } from "src/services/tna-anomalies/anomaly-tracking-by-employee-api.service";
import { AUTHORIZATIONS } from "src/utils/constants/authorization-constants";
import { FT_TNALT } from "src/utils/constants/filter-tables-constants";
import { intlFormatDateShort } from "src/utils/time-utils";
import { toastifyResponse } from "src/utils/toastify-response/toastify-response";
import { v4 as uuidv4 } from "uuid";
interface Props {
  employee?: EmployeeRef | null;
  handleNotifyParent: () => void;
  tableHeight: number;
  handleClose: () => void;
  days?: CustomDate[];
}

export interface State {
  loading: boolean;
  error: string;
  localItems: AnomalyTrackingDTO[];
  lastAnomalyNotificationDate: Date | null;
  lastSignoffDate: Date | null;
  employee: EmployeeRef | null;
  allEmployees: boolean;
  employeeQfId: string;
}

const SingleEmployeeAnomaliesListTable = ({ employee, handleNotifyParent, tableHeight, handleClose, days }: Props) => {
  const { t } = useTranslation();
  const tRP = "components.my_team_breaks.anomalies_list_table"; // tRP - translation root path
  const theme = useTheme();

  const hasAccess = useAuthorization([
    AUTHORIZATIONS.EmployeesAccess.CreateEmployees,
    AUTHORIZATIONS.EmployeesAccess.ConfigureEmployeeGroupParameters,
    AUTHORIZATIONS.LegalEntityAccess.ProcessPayroll,
  ]);

  const [state, setState] = useState<State>({
    loading: false,
    error: "",
    localItems: [],
    lastAnomalyNotificationDate: null,
    lastSignoffDate: null,
    employee: employee || null,
    allEmployees: true,
    employeeQfId: uuidv4(),
  });

  const { anomalyTypesQuickFilter, setAnomalyTypesQuickFilter, allAnomalyTypes, anomalyTypeQFIsLoading } =
    useAnomalyTypesQuickFilter(true);

  const { notificationStatusQuickFilter, setNotificationStatusQuickFilter } = useNotificationStatusQuickFilter();

  const { daysQuickFilter, setDaysQuickFilter, selectedDays } = useChangeDays();

  const allowedToViewOnly = useAuthorization([AUTHORIZATIONS.EmployeesAccess.ViewNonSalaryAndCompensationData]);

  const selectedRowsDataRef = useRef<AnomalyTrackingDTO[]>([]);

  const handleRowSelection = (selectedItems: AnomalyTrackingDTO[]) => {
    selectedRowsDataRef.current = selectedItems;
  };

  const handleLocalCommentUpdate = useCallback(
    (updatedItem: AnomalyTrackingDTO) => {
      if (state.localItems.find((item) => item.id === updatedItem.id)) {
        setState((prev) => ({
          ...prev,
          localItems: [...prev.localItems.filter((item) => item.id !== updatedItem.id), updatedItem],
        }));
      } else {
        setState((prev) => ({
          ...prev,
          localItems: [...prev.localItems, updatedItem],
        }));
      }
    },
    [state.localItems]
  );

  const CommentCellRenderer = ({ row }: { row: AnomalyTrackingDTO }) => {
    const localItem = state.localItems.find((item) => item.id === row.id) || row;

    return (
      <Grid
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          height: "100%",
        }}
      >
        {localItem?.comment && (
          <Grid
            style={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              width: "calc(100% - 40px)",
            }}
          >
            <Typography
              variant="body1"
              style={{ flex: 1 }}
            >
              {localItem?.comment}
            </Typography>
          </Grid>
        )}

        <AddEditComment
          item={localItem}
          effect="edit"
          onLocalSave={handleLocalCommentUpdate}
          disabled={!hasAccess && allowedToViewOnly}
          viewMode={!hasAccess && allowedToViewOnly}
        >
          <IconButton
            size="small"
            title="Edit comment"
          >
            <FontAwesomeIcon
              size="lg"
              icon={faPenToSquare}
              color={theme.palette.primary.main}
            />
          </IconButton>
        </AddEditComment>
      </Grid>
    );
  };

  const CommentCellMemoizedRenderer = React.memo(CommentCellRenderer, (prevProps, nextProps) => {
    const prevLocalItem = state.localItems.find((item) => item.id === prevProps.row.id) || prevProps.row;
    const nextLocalItem = state.localItems.find((item) => item.id === nextProps.row.id) || nextProps.row;
    return prevLocalItem?.comment === nextLocalItem?.comment;
  });

  const handleSave = useCallback(async () => {
    if (selectedRowsDataRef.current.length === 0) {
      const data = {
        id: "",
        correlationId: "",
        invokerId: "",
        isSucceeded: false,
        isCanceled: false,
        error: t("components.toast.no_employee_anomalies_selected"),
      };
      toastifyResponse(data, "failed");
      return;
    }

    setState((prev) => ({ ...prev, loading: true }));

    try {
      const apiService = new SendAnomalyTrackingNotificationToEmployeesApiService();

      const selectedNotificationRowsData = selectedRowsDataRef.current.map((selectedItem) => {
        const latestItem = state.localItems.find((item) => item.id === selectedItem.id) || selectedItem;

        return {
          id: latestItem.id,
          companyAccount: latestItem.companyAccount,
          legalEntity: latestItem.legalEntity,
          employee: latestItem.employee,
          comment: latestItem.comment,
        } as AnomalyTrackingCommentSTO;
      });

      const response = await apiService.saveItems(selectedNotificationRowsData);

      if (response.isSucceeded) {
        selectedRowsDataRef.current = [];
        setState((prev) => ({
          ...prev,
          loading: false,
          error: "",
        }));
        toastifyResponse(response, "all", "employees_notified_successfully");
        handleNotifyParent?.();
      }
    } catch (error) {
      selectedRowsDataRef.current = [];
      setState((prev) => ({
        ...prev,
        error: error as string,
        loading: false,
      }));
      console.error(error);
    }
  }, [handleNotifyParent, state.localItems]);

  const columns: GridColDef<AnomalyTrackingDTO>[] = [
    {
      field: "anomalyDate",
      headerName: t("common_words.date"),
      headerAlign: "center",
      sortable: true,
      disableColumnMenu: true,
      valueGetter: ({ row }) => intlFormatDateShort(new Date(row?.year, row?.month - 1, row?.day)),
      flex: 1,
      align: "center",
    },
    {
      field: "anomalyDescription",
      headerName: t("common_words.anomaly"),
      headerAlign: "center",
      sortable: false,
      disableColumnMenu: true,
      valueGetter: ({ row }) => row?.anomalyType?.description ?? "",
      flex: 2,
      align: "left",
    },
    {
      field: "comment",
      // headerName: t("common_words.comment"),
      renderHeader: () => (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Typography>{t("common_words.comment")}</Typography>
          <Typography sx={{ fontSize: "11px !important" }}>{t(`${tRP}.comment_notification`)}</Typography>
        </div>
      ),
      headerAlign: "center",
      sortable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => <CommentCellMemoizedRenderer row={row} />,
      flex: 2,
      align: "center",
    },
    {
      field: "lastNotifiedOn",
      headerName: t("common_words.last_notified_on"),
      headerAlign: "center",
      sortable: true,
      disableColumnMenu: true,
      valueGetter: ({ row }) => (row.lastNotifiedOn ? intlFormatDateShort(new Date(row.lastNotifiedOn)) : ""),
      flex: 1,
      align: "center",
    },
  ];

  const quickFilters = React.useMemo(
    () => (
      <QuickfiltersContainer>
        <AnomalyTypesQuickfilter
          quickFilters={anomalyTypesQuickFilter}
          handleChangeQuickFilters={setAnomalyTypesQuickFilter}
          allAnomalyTypesOption
          allAnomalyTypes={allAnomalyTypes}
          isMultiSelect
        />
        <NotificationStatusQuickfilter
          quickFilters={notificationStatusQuickFilter}
          handleChangeQuickFilters={setNotificationStatusQuickFilter}
        />
        {days?.length === 0 && (
          <FilterByDays
            quickFilters={daysQuickFilter}
            handleChangeQuickFilters={setDaysQuickFilter}
            filterColor="filter"
            withMaxDate={false}
            maxSelectableDays={31}
          />
        )}
      </QuickfiltersContainer>
    ),
    [
      allAnomalyTypes,
      anomalyTypesQuickFilter,
      days,
      daysQuickFilter,
      notificationStatusQuickFilter,
      setAnomalyTypesQuickFilter,
      setDaysQuickFilter,
      setNotificationStatusQuickFilter,
    ]
  );

  const propsFilter = useMemo(
    () =>
      ({
        employeesIds: [employee?.id],
        days: days?.length !== 0 ? days : selectedDays,
        typeIds: anomalyTypesQuickFilter.anomalyTypes?.anomalyTypeIds || [],
        isNotified: notificationStatusQuickFilter.notificationStatus?.isNotified,
        isAbnormalPresence: false,
        isResolved: false,
      } as AnomalyTrackingFilter),
    [
      employee?.id,
      days,
      selectedDays,
      anomalyTypesQuickFilter.anomalyTypes?.anomalyTypeIds,
      notificationStatusQuickFilter.notificationStatus?.isNotified,
    ]
  );

  return (
    <LoadingOverlay loading={state.loading || anomalyTypeQFIsLoading}>
      {/* <DialogTitle
        title={`${t(`${tRP}.employee_tna_config`)}`}
        // handleClose={() => setState((state) => ({ ...state, isOpen: false }))}
        additionalSection={ */}
      {/* {employee?.id && (
        <Grid
          container
          sx={{ alignItems: "center" }}
          spacing={2}
        >
          <Grid
            item
            xs={6}
          >
            <ConcatenatedCell
              aligned={"left"}
              FirstElement={
                <Grid>
                  <Typography>{employee?.name}</Typography>
                  {employee?.birthDate && (
                    <Typography variant={"body2"}>
                      <FontAwesomeIcon
                        icon={faBirthdayCake}
                        size="xs"
                      />{" "}
                      &nbsp;
                      {intlFormatDateShort(employee?.birthDate)}
                    </Typography>
                  )}
                </Grid>
              }
              SecondElement={
                <Grid>
                  <Typography
                    style={{ color: theme.palette.text.disabled }}
                    variant={"body2"}
                    title={employee?.legalEntity?.name}
                  >
                    {employee?.legalEntity?.code ?? employee?.legalEntity?.name}
                  </Typography>
                </Grid>
              }
            />
          </Grid>
          <Grid
            item
            xs={6}
            sx={{ textAlign: "right" }}
          >
            <Typography
              variant="body2"
              style={{ color: theme.palette.text.secondary }}
            >
              {t("common_words.last_anomaly_notification_sent_on")}
              {": "}
              {state?.lastAnomalyNotificationDate ? intlFormatDateShort(state.lastAnomalyNotificationDate) : "N/A"}
            </Typography>
            <Typography
              variant="body2"
              style={{ color: theme.palette.text.secondary }}
            >
              {t("common_words.last_signoff_date")}
              {": "}
              {state?.lastSignoffDate ? intlFormatDateShort(state.lastSignoffDate) : "N/A"}
            </Typography>
          </Grid>
        </Grid>
      )} */}
      {/* }
      /> */}
      <Table
        hideRemoveIcon
        translationRootPath={tRP}
        displayTitle={false}
        columns={columns}
        allowEdit={false}
        readOnly
        defaultPageSize={10}
        tableHeight={tableHeight}
        serviceApi={new AnomalyTrackingByEmployee()}
        enableSelection
        handleCheckedRows={handleRowSelection}
        propsFilter={propsFilter}
        quickFilters={quickFilters}
        filter={FT_TNALT}
      />
      <DialogActions
        handleCancel={() => {
          selectedRowsDataRef.current = [];
          setState((prev) => ({ ...prev, error: "" }));
          handleClose();
        }}
        handleSave={handleSave}
        disabledSaveButton={state.loading}
        disabledCancelButton={state.loading}
        customSaveName={t(`${tRP}.send_notifications`)}
      />
    </LoadingOverlay>
  );
};

export default React.memo(SingleEmployeeAnomaliesListTable);
